import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  api, ApiError, type CombinedSettings, type MessagesPreview, type MessagesPreviewIn, type OrgSettings,
} from '../lib/api'

/** EVERYTHING A MEMBER'S PHONE RECEIVES ABOUT A CALL, FOUR SHORT PAGES (1 Nov 2026).
 *
 *  It was one page of four cards -- the header and footer, the message to
 *  the member going in the organization's own words (084), the switches it
 *  replaced, and the combined rebroadcast -- and the customer: "There is too
 *  much settings already to do. I need to scroll down more and more and more
 *  and more." `messages-tabs-mockup.html`. Now:
 *
 *    Broadcast           the header and footer, each channel's own, and
 *                        whether they go round the messages to him
 *    Member going        the message when he accepts: his own words OR the
 *                        switches -- only the way in use is drawn (his Q2 A)
 *    Thank-you & closed  the thank-you line, the closed-call message, and
 *                        telling a member taken off a call
 *    Rebroadcast         the combined rebroadcast
 *
 *  Picked by the tabs on top AND by the list under "Call messages" in the
 *  Settings column (his own answer: both), so the page is told which one by
 *  `Settings` and says when a tab is pressed.
 *
 *  ONE SAVE PER PAGE, at the bottom, always in view. The switches that used
 *  to save on their own press wait for it now, like the words beside them:
 *  one rule for the page. What is typed on one page STAYS while another is
 *  looked at -- the four share one draft -- and a page with something not
 *  saved says so with a dot, on its tab and in the column.
 *
 *  EVERY PHONE IS DRAWN BY THE SERVER, from what is being typed, by the
 *  composers the send uses (`/api/org-settings/member-message/preview`), so
 *  what the page shows is what goes. */

export type MessagePage = 'broadcast' | 'going' | 'thanks' | 'rebroadcast'

/** The four, in order. `Settings` draws the same list in its column. */
export const MESSAGE_PAGES: { key: MessagePage; label: string; sub: string }[] = [
  { key: 'broadcast', label: 'Broadcast', sub: 'header & footer, to everyone' },
  { key: 'going', label: 'Member going', sub: 'the details when he accepts' },
  { key: 'thanks', label: 'Thank-you & closed', sub: 'and taken off a call' },
  { key: 'rebroadcast', label: 'Rebroadcast', sub: 'several calls in one message' },
]

type SwitchKey = 'handover_code' | 'handover_address' | 'handover_map_link'
  | 'handover_cross_streets' | 'handover_location_note'
  | 'handover_caller_name' | 'handover_responder_notes'

/** The switches (057, 058, 060), in the order their lines appear in the
 *  message. Each says what it does in one line: the phone beside them shows
 *  the rest. */
const SWITCHES: { key: SwitchKey; label: string; on: string; off: string }[] = [
  { key: 'handover_code', label: 'The call code at the top, instead of the nature in words',
    on: 'The code as the radio says it, “FFT”. A nature with no code keeps its name.',
    off: 'The nature in words, “Flat tire”.' },
  { key: 'handover_address', label: 'The full address, with the house number',
    on: 'And the apartment, when one was typed.',
    off: 'The street without the number, or the pressed area — what the broadcast gave.' },
  { key: 'handover_map_link', label: 'A map link after the address',
    on: 'One Google Maps link: the pin when the call was placed on the map, else the full address.',
    off: 'No link.' },
  { key: 'handover_cross_streets', label: 'The two cross streets', on: '“between … and …”', off: 'No cross streets.' },
  { key: 'handover_location_note', label: 'The notes about the location, as “Directions:”',
    on: 'Never in the broadcast either way.', off: 'The dispatcher gives them over the air.' },
  { key: 'handover_caller_name', label: 'The caller’s name',
    on: 'Never an anonymous caller’s. The callback number has its own button.',
    off: 'No “Caller:” line. The callback number has its own button.' },
  { key: 'handover_responder_notes', label: 'The note written for the responder on an answer',
    on: 'Under the answer it was written on.', off: 'The answer goes without its note.' },
]

type Own = { header: string; footer: string } | null
type Defaults = NonNullable<OrgSettings['member_defaults']>
/** Everything on the four pages, as typed. */
type Draft = {
  header: string
  footer: string
  /** Each channel's own pair; null = the organization's. */
  own: Record<string, Own>
  wrapped: boolean
  mode: 'words' | 'switches'
  template: string
  sw: Record<SwitchKey, boolean>
  thanks: string
  onAccept: boolean
  onEvery: boolean
  onClose: boolean
  closed: string
  removed: boolean
  combined: CombinedSettings
}

const FIELDS: Record<MessagePage, (keyof Draft)[]> = {
  broadcast: ['header', 'footer', 'own', 'wrapped'],
  going: ['mode', 'template', 'sw'],
  thanks: ['thanks', 'onAccept', 'onEvery', 'onClose', 'closed', 'removed'],
  rebroadcast: ['combined'],
}

const FALLBACK: Defaults = { template: '', thanks: '', closed: '', blanks: [] }

/** What the server last said, as the pages draw it. ONE reader, for the
 *  first load and every save: two copies of this drift. */
function draftOf(s: OrgSettings, d: Defaults): Draft {
  return {
    header: s.broadcast_header ?? '',
    footer: s.broadcast_footer ?? '',
    own: Object.fromEntries(s.channels.map((c) => [c.channel,
      c.broadcast_header === null && c.broadcast_footer === null
        ? null : { header: c.broadcast_header ?? '', footer: c.broadcast_footer ?? '' }])),
    wrapped: !!s.handover_wrapped,
    mode: s.member_template !== null ? 'words' : 'switches',
    template: s.member_template ?? d.template,
    sw: Object.fromEntries(SWITCHES.map((x) => [x.key, !!s[x.key]])) as Record<SwitchKey, boolean>,
    // A LINE NEVER SAVED STARTS EMPTY, with the suggested one offered beside
    // it (1 Nov 2026). It started filled in (084), which made a page that
    // was never touched read as "not saved" -- and an emptied, saved box
    // come back filled, since an empty line is stored as none.
    thanks: s.member_thanks ?? '',
    onAccept: !!s.thanks_on_accept,
    onEvery: !!s.thanks_on_every,
    onClose: !!s.thanks_on_close,
    closed: s.closed_template ?? d.closed,
    removed: !!s.tell_removed_member,
    // What is stored from before the setting has no place: it goes under, and
    // the page must not read as changed when Under is pressed.
    combined: { ...s.combined_rebroadcast, line_place: s.combined_rebroadcast.line_place ?? 'under' },
  }
}

/** Equal whatever order the keys came in: Postgres hands a jsonb object back
 *  with its keys sorted by length ("one, more, none"), and the page builds
 *  them in its own order -- a page must not read as changed for that (the
 *  review). */
const canon = (v: unknown) => JSON.stringify(v, (_, x) => (x && typeof x === 'object' && !Array.isArray(x)
  ? Object.fromEntries(Object.keys(x).sort().map((k) => [k, (x as Record<string, unknown>)[k]])) : x))
const same = (a: unknown, b: unknown) => canon(a) === canon(b)

/** Whether a page has something not saved. On Member going the text counts
 *  only when it is the way being saved: a text typed and then left for the
 *  switches is kept for coming back to, and saving would change nothing. */
function changed(page: MessagePage, d: Draft, base: Draft): boolean {
  if (page === 'going') {
    return d.mode !== base.mode || !same(d.sw, base.sw)
      || (d.mode === 'words' && d.template !== base.template)
  }
  return FIELDS[page].some((k) => !same(d[k], base[k]))
}

/** One page's fields as the settings PATCH takes them. */
function patchFor(page: MessagePage, d: Draft, base: Draft): Record<string, unknown> {
  if (page === 'broadcast') {
    // A channel goes only when its own wording changed: null is "use the
    // organization's", "" is "this channel sends none" (053).
    const channels = Object.fromEntries(Object.keys(d.own)
      .filter((c) => !same(d.own[c], base.own[c]))
      .map((c) => [c, d.own[c]
        ? { broadcast_header: d.own[c]!.header, broadcast_footer: d.own[c]!.footer }
        : { broadcast_header: null, broadcast_footer: null }]))
    return {
      broadcast_header: d.header, broadcast_footer: d.footer, handover_wrapped: d.wrapped,
      ...(Object.keys(channels).length ? { channels } : {}),
    }
  }
  if (page === 'going') {
    // "" is the server's "back to the message from the switches" (084).
    return { member_template: d.mode === 'words' ? d.template : '', ...d.sw }
  }
  if (page === 'thanks') {
    // THE CLOSED MESSAGE ONLY WHEN IT WAS CHANGED (the review): a NULL one is
    // the starting text, and sending that text back stored it as the
    // organization's own -- so ticking "tell a member taken off" would have
    // pinned it.
    return {
      member_thanks: d.thanks, thanks_on_accept: d.onAccept, thanks_on_every: d.onEvery,
      thanks_on_close: d.onClose, tell_removed_member: d.removed,
      ...(d.closed !== base.closed ? { closed_template: d.closed } : {}),
    }
  }
  return { combined_rebroadcast: d.combined }
}

/** What the preview is handed: all four pages, so every phone is drawn. */
function previewOf(d: Draft): MessagesPreviewIn {
  return {
    template: d.template, thanks: d.thanks, thanks_on_accept: d.onAccept, closed_template: d.closed,
    thanks_on_every: d.onEvery, header: d.header, footer: d.footer, wrapped: d.wrapped,
    channels: Object.fromEntries(Object.entries(d.own).map(([c, o]) => [c,
      o ? { broadcast_header: o.header, broadcast_footer: o.footer }
        : { broadcast_header: null, broadcast_footer: null }])),
    switches: d.sw, combined: d.combined,
  }
}

export function Messages({ page, onPage, onDirty, hidden }: {
  page: MessagePage
  onPage: (page: MessagePage) => void
  /** The pages with something not saved, for the dots in the column. */
  onDirty: (pages: MessagePage[]) => void
  /** Kept on screen-but-hidden while another Settings page is looked at, so
   *  what is typed here is not lost by a look at the phone book. */
  hidden?: boolean
}) {
  const [settings, setSettings] = useState<OrgSettings | null>(null)
  const [defaults, setDefaults] = useState<Defaults>(FALLBACK)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [said, setSaid] = useState<{ page: MessagePage; error: string | null } | null>(null)
  const [preview, setPreview] = useState<MessagesPreview | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)

  // READ WHEN SHOWN, and again on the next showing if that failed (the
  // review): the page stays mounted now, so a mount alone would never retry.
  useEffect(() => {
    if (hidden || settings) return
    api.orgSettings()
      .then((s) => {
        const d = s.member_defaults ?? FALLBACK
        setSettings(s)
        setDefaults(d)
        setDraft(draftOf(s, d))
        setLoadError(null)
      })
      .catch((e) => setLoadError(e instanceof ApiError ? e.message : 'Could not load settings.'))
  }, [hidden]) // eslint-disable-line react-hooks/exhaustive-deps

  const base = useMemo(() => (settings ? draftOf(settings, defaults) : null), [settings, defaults])
  const dirty = useMemo(() => (draft && base
    ? MESSAGE_PAGES.map((p) => p.key).filter((k) => changed(k, draft, base)) : []), [draft, base])
  const dirtyKey = dirty.join(',')
  useEffect(() => { onDirty(dirty) }, [dirtyKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // THE PHONES FOLLOW THE TYPING, a moment after it stops, from the server --
  // and not while the page is hidden behind another Settings page.
  const wanted = draft ? JSON.stringify(previewOf(draft)) : ''
  useEffect(() => {
    if (!draft || hidden) return
    let alive = true
    const t = setTimeout(() => {
      api.memberMessagePreview(previewOf(draft))
        .then((p) => { if (alive) { setPreview(p); setPreviewError(null) } })
        .catch((e) => { if (alive) setPreviewError(e instanceof ApiError ? e.message : 'The preview could not be drawn.') })
    }, 350)
    return () => { alive = false; clearTimeout(t) }
  }, [wanted, hidden]) // eslint-disable-line react-hooks/exhaustive-deps

  const set = (p: Partial<Draft>) => {
    setDraft((d) => (d ? { ...d, ...p } : d))
    setSaid(null)
  }

  async function save() {
    if (!draft || !base) return
    setBusy(true)
    setSaid(null)
    try {
      const next = await api.orgSettingsUpdate(patchFor(page, draft, base))
      setSettings(next)
      // THIS PAGE TAKES THE SERVER'S ANSWER (the words as stored, trimmed);
      // the others keep what is being typed on them. A text left for the
      // switches stays in its box for coming back to.
      const fresh = draftOf(next, defaults)
      setDraft((d) => {
        if (!d) return d
        const out = { ...d }
        for (const k of FIELDS[page]) {
          if (k === 'template' && page === 'going' && d.mode === 'switches') continue
          ;(out as Record<string, unknown>)[k] = fresh[k]
        }
        return out
      })
      setSaid({ page, error: null })
    } catch (e) {
      setSaid({ page, error: e instanceof ApiError ? e.message : 'Could not save.' })
    } finally {
      setBusy(false)
    }
  }

  function undo() {
    if (!draft || !base) return
    set(Object.fromEntries(FIELDS[page].map((k) => [k, base[k]])) as Partial<Draft>)
  }

  const here = MESSAGE_PAGES.find((p) => p.key === page)!
  const isDirty = dirty.includes(page)
  // AN EMPTY TEXT IS NOT SAVED AS YOUR OWN WORDS (the review): the server
  // reads "" as "back to the switches", which would say "Saved." and flip
  // the page. The 084 card refused it the same way.
  const empty = page === 'going' && draft?.mode === 'words' && !draft.template.trim()
  const others = MESSAGE_PAGES.filter((p) => p.key !== page && dirty.includes(p.key)).map((p) => p.label)
  const status = said?.page === page
    ? (said.error ? `Not saved: ${said.error}` : 'Saved.')
    : empty ? 'Write the message first — an empty one cannot be saved.'
    : isDirty ? 'Changes not saved on this page.' : 'Nothing changed on this page.'

  return (
    <div className="msgpage" hidden={hidden}>
      <div className="msgpagehead">
        <h2>Call messages</h2>
        <span>What a member’s phone receives about a call</span>
      </div>
      <nav className="msgtabs" aria-label="Call messages">
        {MESSAGE_PAGES.map((p) => (
          <button key={p.key} type="button" aria-current={p.key === page ? 'page' : undefined}
                  className={`msgpagetab ${p.key === page ? 'on' : ''}`} onClick={() => onPage(p.key)}>
            <span><b>{p.label}</b><small>{p.sub}</small></span>
            {dirty.includes(p.key) && <i className="msgdot" title="Changes not saved" aria-label="Changes not saved" />}
          </button>
        ))}
      </nav>

      <div className="msgbody">
        {loadError && <div className="notice">{loadError}</div>}
        {draft && settings && (
          <>
            <div className="msgsplit">
              {/* STILL WHILE SAVING (the review): the answer replaces this
                  page's fields, so letters typed meanwhile -- or a click on
                  the other way of writing it -- would be lost. */}
              <fieldset className="msged" disabled={busy}>
                {page === 'broadcast' && <BroadcastPage d={draft} set={set} settings={settings} />}
                {page === 'going' && (
                  <GoingPage d={draft} set={set} inUse={base!.mode} defaults={defaults}
                             words={preview?.words ?? []} unknown={preview?.unknown ?? []} />
                )}
                {page === 'thanks' && <ThanksPage d={draft} set={set} defaults={defaults} />}
                {page === 'rebroadcast' && <RebroadcastPage d={draft} set={set} />}
              </fieldset>
              <aside className="msgpv" aria-label="What it looks like on a phone">
                {previewError ? <p className="mm-warn">{previewError}</p>
                  : <Phones page={page} d={draft} preview={preview} settings={settings} />}
              </aside>
            </div>
            <div className="msgsave">
              <button type="button" className="primary compact" disabled={busy || !isDirty || empty} onClick={save}>
                {busy ? 'Saving…' : `Save — ${here.label}`}
              </button>
              <span className={`msgstatus ${said?.page === page && said.error ? 'bad' : isDirty ? 'dirty' : ''}`}>
                {status}{others.length > 0 && ` Also not saved: ${others.join(', ')}.`}
              </span>
              {isDirty && (
                <button type="button" className="ghost compact msgundo" disabled={busy} onClick={undo}>
                  Undo my changes
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

type PageProps = { d: Draft; set: (p: Partial<Draft>) => void }

/** The long explanation, behind one line. */
function More({ children }: { children: ReactNode }) {
  return (
    <details className="msgmore">
      <summary>More about this</summary>
      <div>{children}</div>
    </details>
  )
}

function Tick({ checked, onChange, label, hint, disabled }: {
  checked: boolean; onChange?: (on: boolean) => void; label: ReactNode; hint?: ReactNode; disabled?: boolean
}) {
  return (
    <label className={`msgtick ${disabled ? 'fixed' : ''}`}>
      <input type="checkbox" checked={checked} disabled={disabled}
             onChange={(e) => onChange?.(e.target.checked)} />
      <span>{label}{hint && <small>{hint}</small>}</span>
    </label>
  )
}

/** Put `{name}` where the cursor is in one box. */
function useInsert(value: string, onValue: (v: string) => void) {
  const box = useRef<HTMLTextAreaElement>(null)
  const insert = (name: string) => {
    const el = box.current
    const blank = `{${name}}`
    const at = el?.selectionStart ?? value.length
    const end = el?.selectionEnd ?? at
    onValue(value.slice(0, at) + blank + value.slice(end))
    requestAnimationFrame(() => {
      if (!el) return
      el.focus()
      el.selectionStart = el.selectionEnd = at + blank.length
    })
  }
  return { box, insert }
}

/* ---- Broadcast ----------------------------------------------------------- */

/** THE WORDING ROUND EVERY BROADCAST. The customer: "it should be a header on
 *  the text message that I can set somewhere in the settings ... then it
 *  should be the whole broadcast that I'm sending out, then it should be a
 *  footer ... applied to email, text messages, WhatsApp, and whatever way
 *  it's going to be broadcasted." THE CALL ITSELF IS NOT EDITABLE HERE: it is
 *  composed, and identical on every channel. */
function BroadcastPage({ d, set, settings }: PageProps & { settings: OrgSettings }) {
  const footer = useInsert(d.footer, (v) => set({ footer: v }))
  return (
    <>
      <p className="msglead">Wrapped round every call you send out, on every channel. Leave a box empty to send nothing there.</p>
      <div className="field">
        <label htmlFor="msg-hdr">Header — the first line</label>
        <textarea id="msg-hdr" rows={2} maxLength={200} value={d.header}
                  onChange={(e) => set({ header: e.target.value })} />
      </div>
      <div className="field">
        <label htmlFor="msg-ftr">Footer — the last line</label>
        <textarea id="msg-ftr" ref={footer.box} rows={2} maxLength={200} value={d.footer}
                  onChange={(e) => set({ footer: e.target.value })} />
        <p className="msghint">
          <button type="button" className="mm-chip" onClick={() => footer.insert('dispatcher')}
                  title="Put it in the footer">{'{dispatcher}'}</button>{' '}
          is the name of the dispatcher who sent it. The footer goes on the first broadcast only.
        </p>
      </div>
      <More>
        A text is cut past 1,600 characters, so both are kept short — and the footer comes last,
        after the call, so a cut message loses the footer before it loses the address. Each box
        goes out as one line. A re-broadcast keeps the header and drops the footer. To see a whole
        message with your own questions in it, use <b>Settings → Nature of call → the broadcast text</b>.
      </More>

      {/* PER CHANNEL, for the one he wants to differ -- his answer when asked
          whether it was one pair or one per channel was both. Only channels
          the operator has wired up are drawn: wording for a channel that does
          not exist reaches nobody and reads as though it does. */}
      {settings.channels.length > 0 && (
        <>
          <h3 className="msgh">Different wording for one channel</h3>
          {settings.channels.map((c) => {
            const mine = d.own[c.channel] ?? null
            const name = c.channel === 'sms' ? 'Text messages' : 'Email'
            return (
              <div className="msgchan" key={c.channel}>
                <label>
                  <span>{name}{!c.enabled && <em> switched off</em>}</span>
                  <select value={mine ? 'own' : 'same'}
                          onChange={(e) => set({ own: { ...d.own,
                            [c.channel]: e.target.value === 'own' ? { header: d.header, footer: d.footer } : null } })}>
                    <option value="same">Same as above</option>
                    <option value="own">Its own header and footer</option>
                  </select>
                </label>
                {mine && (
                  <div className="msgchanboxes">
                    <textarea rows={2} maxLength={200} value={mine.header} aria-label={`Header for ${name.toLowerCase()}`}
                              placeholder="Header — empty for none"
                              onChange={(e) => set({ own: { ...d.own, [c.channel]: { ...mine, header: e.target.value } } })} />
                    <textarea rows={2} maxLength={200} value={mine.footer} aria-label={`Footer for ${name.toLowerCase()}`}
                              placeholder="Footer — empty for none"
                              onChange={(e) => set({ own: { ...d.own, [c.channel]: { ...mine, footer: e.target.value } } })} />
                    <p className="msghint">Empty here means this channel sends none — not the same as “Same as above”.</p>
                  </div>
                )}
              </div>
            )
          })}
        </>
      )}

      {/* THE QUESTION HELD SINCE 16 SEP, answered 18 Sep 2026: a switch, on by
          default, so another customer keeps the wrapper and this one gets
          "the message should be clean". The broadcast itself is always
          wrapped; this is the messages to the member going. */}
      <h3 className="msgh">Messages to the member going</h3>
      <Tick checked={d.wrapped} onChange={(on) => set({ wrapped: on })}
            label="Put the header and footer round them too"
            hint={d.wrapped
              ? 'The details from the switches, the site notes, the callback number, the notes and “no longer on this call”.'
              : 'They go bare. The broadcast keeps its header and footer.'} />
      {d.mode === 'words' && (
        <p className="msghint">Your own words on Member going are always sent as written, without them.</p>
      )}
    </>
  )
}

/* ---- Member going ----------------------------------------------------------- */

/** THE MESSAGE WHEN HE ACCEPTS: the organization's own words (084) or the
 *  switches (057) -- and ONLY THE ONE IN USE IS DRAWN (his Q2 A, 1 Nov 2026):
 *  with both on one page, half of it did nothing and said so in a paragraph.
 *  Picking the other way changes nothing until Save. */
function GoingPage({ d, set, inUse, defaults, words, unknown }: PageProps & {
  inUse: Draft['mode']; defaults: Defaults; words: string[]; unknown: string[]
}) {
  const text = useInsert(d.template, (v) => set({ template: v }))
  const sw = (k: SwitchKey, on: boolean) => set({ sw: { ...d.sw, [k]: on } })
  return (
    <>
      <h3 className="msgh first">How the message is written</h3>
      <div className="msgmode">
        <div className="msgseg" role="group" aria-label="How the message is written">
          <button type="button" className={d.mode === 'words' ? 'on' : ''} aria-pressed={d.mode === 'words'}
                  onClick={() => set({ mode: 'words' })}>In your own words</button>
          <button type="button" className={d.mode === 'switches' ? 'on' : ''} aria-pressed={d.mode === 'switches'}
                  onClick={() => set({ mode: 'switches' })}>Built from switches</button>
        </div>
        <span className={`msgchip ${d.mode === inUse ? 'on' : ''}`}>
          {d.mode === inUse ? 'In use now' : 'Not in use until you save'}
        </span>
      </div>
      <p className="msglead">Sent to the member logged as going, and by “Resend full address &amp; details”.</p>

      {d.mode === 'words' ? (
        <>
          <textarea ref={text.box} className="msg-text" value={d.template} spellCheck={false} maxLength={4000}
                    aria-label="The message to the member going"
                    onChange={(e) => set({ template: e.target.value })} />
          <div className="mm-chips" aria-label="Put a blank in">
            {defaults.blanks.map((b) => (
              <button type="button" key={b} className="mm-chip" onClick={() => text.insert(b)}>{`{${b}}`}</button>
            ))}
            {words.map((w) => (
              <button type="button" key={`w-${w}`} className="mm-chip word"
                      title="A question's short word: its answer" onClick={() => text.insert(w)}>{`{${w}}`}</button>
            ))}
          </div>
          <p className="msghint">
            A line whose blank is empty on a call is left out. Words in <b>[ ]</b> go alone. The grey
            blanks are your questions’ short words.{' '}
            <button type="button" className="msglink" disabled={d.template === defaults.template}
                    onClick={() => set({ template: defaults.template })}>Back to the starting text</button>
          </p>
          {unknown.length > 0 && (
            <p className="mm-warn">
              Nothing fills {unknown.map((u) => `{${u}}`).join(', ')} — a typo, or a short word no question
              has yet. Lines with it are left out until it is filled.
            </p>
          )}
          <More>
            Every member going gets his own copy, with his own unit. A question’s short word (set on the
            question, under Nature of call) is a blank too: <b>{'{Where}'}</b> is the answer to the
            question whose short word is “Where”, written small inside a sentence. <b>{'{answers}'}</b> is
            every other answer, one per line. The notes written for him always go — at <b>{'{notes}'}</b>, or
            last. The caller’s number is never a blank. Your words go without the header and footer.
          </More>
          <h3 className="msgh">Still counts with your own words</h3>
          <Tick checked={d.sw.handover_responder_notes} onChange={(on) => sw('handover_responder_notes', on)}
                label="The note written for the responder on an answer"
                hint="Under the answer, wherever your text puts it." />
        </>
      ) : (
        <>
          <Tick checked disabled label="The nature of the call" hint="Always — it is what the message is about." />
          {SWITCHES.map((x) => (
            <Tick key={x.key} checked={d.sw[x.key]} onChange={(on) => sw(x.key, on)}
                  label={x.label} hint={d.sw[x.key] ? x.on : x.off} />
          ))}
          <p className="msghint">
            The answers to the call questions are ticked one question at a time, under <b>Nature of call</b>:
            “send the answer to the member who accepts the call”.
          </p>
        </>
      )}
    </>
  )
}

/* ---- Thank-you & closed --------------------------------------------------- */

/** THE THANK-YOU LINE AND THE MESSAGES AFTER THE CALL (084, 080). The
 *  customer's example ended "Thank U for taking the call, Keep it up. Tizke
 *  Lemitzvos", and he wanted to choose "if you want it on every message or
 *  only after accepting the call ... maybe it should also be by closing a
 *  call". */
function ThanksPage({ d, set, defaults }: PageProps & { defaults: Defaults }) {
  return (
    <>
      <p className="msglead">A thank-you to the member who went, and the short messages after the call.</p>
      <div className="field">
        <label htmlFor="msg-thx">The thank-you line</label>
        <input id="msg-thx" value={d.thanks} maxLength={300} placeholder="Empty: no thank-you"
               onChange={(e) => set({ thanks: e.target.value })} />
        {!d.thanks.trim() && defaults.thanks && (
          <p className="msghint">
            Suggested: “{defaults.thanks}”{' '}
            <button type="button" className="msglink" onClick={() => set({ thanks: defaults.thanks })}>Use it</button>
          </p>
        )}
      </div>
      <h3 className="msgh">Add it</h3>
      <Tick checked={d.onAccept} onChange={(on) => set({ onAccept: on })}
            label="At the end of the message when he accepts the call" />
      <Tick checked={d.onEvery} onChange={(on) => set({ onEvery: on })}
            label="At the end of every other message to him"
            hint="Sent again, the call moved, a note, gate codes, the callback number." />

      <hr className="msgrule" />
      <Tick checked={d.onClose} onChange={(on) => set({ onClose: on })}
            label={<><b>When the call is closed</b>, send each member who went a message</>}
            hint="Once, only to members still going who were sent something on that call. The thank-you is added at the end." />
      {d.onClose && (
        <div className="msgindent">
          <textarea className="msg-text short" value={d.closed} spellCheck={false} maxLength={1000}
                    aria-label="The message when the call is closed"
                    onChange={(e) => set({ closed: e.target.value })} />
          <p className="msghint">The same blanks as Member going: {'{unit number}'}, {'{street}'}, {'{address}'}…</p>
        </div>
      )}

      {/* A MEMBER TAKEN OFF A CALL (080), logged as going by mistake. Off by
          default -- the customer's own choice -- for an organization that
          wants him told. */}
      <hr className="msgrule" />
      <Tick checked={d.removed} onChange={(on) => set({ removed: on })}
            label={<><b>When a member is taken off a call</b>, tell him he is no longer on it</>}
            hint={d.removed
              ? 'By text and email — only if he had already been sent something about the call.'
              : 'Nothing is sent. He was logged by mistake and most likely never knew.'} />
    </>
  )
}

/* ---- Rebroadcast ---------------------------------------------------------- */

/** THE COMBINED REBROADCAST (073): the quick buttons the Rebroadcast window
 *  offers, which one each call starts with by how many are going (his Q1 A),
 *  what each call's part of the one message says, and its first line. */
function RebroadcastPage({ d, set }: PageProps) {
  const c = d.combined
  const lines = c.lines
  const edit = (next: CombinedSettings) => set({ combined: next })
  const setLines = (next: CombinedSettings['lines'], remap?: (i: number) => number | null) => {
    const fix = (v: number | null) => (v === null ? null : remap ? remap(v) : v)
    edit({ ...c, lines: next, starts: { none: fix(c.starts.none), one: fix(c.starts.one), more: fix(c.starts.more) } })
  }
  const PARTS: [keyof CombinedSettings['parts'], string][] = [
    ['where', 'Where — the address or the area'],
    ['details', 'Location details (mile marker)'],
    ['code', 'Call code (FFT)'],
    ['urgency', 'Urgency word (URGENT)'],
    ['open', 'How long it has been open'],
    ['going', 'How many are going'],
  ]
  const startRow = (key: keyof CombinedSettings['starts'], label: string) => (
    <label className="msgstart" key={key}>
      <span>{label}</span>
      <select value={c.starts[key] === null ? '' : String(c.starts[key])}
              onChange={(e) => edit({ ...c, starts: { ...c.starts,
                [key]: e.target.value === '' ? null : Number(e.target.value) } })}>
        <option value="">Left out (unticked)</option>
        {lines.map((l, i) => <option key={i} value={String(i)}>{l.text || '(empty)'}</option>)}
      </select>
    </label>
  )
  return (
    <>
      <p className="msglead">
        What the ↻ Rebroadcast button on the Dispatch screen offers: one message for several open
        calls, each with its own line.
      </p>
      <div className="msgcols">
        <div>
          <h3 className="msgh first">The quick buttons</h3>
          {lines.map((l, i) => (
            <div className="msgbtnrow" key={i}>
              <input value={l.text} maxLength={60} aria-label={`Quick button ${i + 1}`}
                     onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
              <label className="check"><input type="checkbox" checked={l.urgent}
                     onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, urgent: e.target.checked } : x)))} /> red</label>
              <button type="button" className="ghost compact msgsm" disabled={i === 0} aria-label="Move up"
                      onClick={() => {
                        const next = [...lines]; [next[i - 1], next[i]] = [next[i], next[i - 1]]
                        setLines(next, (v) => (v === i ? i - 1 : v === i - 1 ? i : v))
                      }}>↑</button>
              <button type="button" className="ghost compact msgsm" disabled={lines.length === 1} aria-label="Remove"
                      onClick={() => setLines(lines.filter((_, j) => j !== i),
                        (v) => (v === i ? null : v > i ? v - 1 : v))}>✕</button>
            </div>
          ))}
          {lines.length < 8 && (
            <button type="button" className="ghost compact msgsm"
                    onClick={() => setLines([...lines, { text: '', urgent: false }])}>+ Add a button</button>
          )}
          <h3 className="msgh">Which button each call starts with</h3>
          {startRow('none', 'Nobody going')}
          {startRow('one', 'One going')}
          {startRow('more', 'Two or more')}
        </div>
        <div>
          <h3 className="msgh first">What each call’s part says</h3>
          <Tick checked disabled label="Nature of call — always" />
          {PARTS.map(([k, label]) => (
            <Tick key={k} checked={c.parts[k]} label={label}
                  onChange={(on) => edit({ ...c, parts: { ...c.parts, [k]: on } })} />
          ))}
          <h3 className="msgh">Where the button’s words go</h3>
          <div className="msgseg" role="group" aria-label="Where the button’s words go">
            <button type="button" className={c.line_place === 'above' ? 'on' : ''} aria-pressed={c.line_place === 'above'}
                    onClick={() => edit({ ...c, line_place: 'above' })}>Above the call</button>
            <button type="button" className={c.line_place === 'under' ? 'on' : ''} aria-pressed={c.line_place === 'under'}
                    onClick={() => edit({ ...c, line_place: 'under' })}>Under the call</button>
          </div>
        </div>
      </div>
      <h3 className="msgh">The first line of the message</h3>
      <div className="field">
        <input value={c.heading} maxLength={120} aria-label="First line of the message"
               onChange={(e) => edit({ ...c, heading: e.target.value })} />
        <p className="msghint">{'{n}'} becomes the number of calls. Your header goes on top; the footer is for a call’s first broadcast only.</p>
      </div>
    </>
  )
}

/* ---- the phones --------------------------------------------------------- */

function Phone({ title, to, children, count }: { title: string; to: string; children: ReactNode; count?: string }) {
  return (
    <div className="mm-phone">
      <div className="mm-top"><b>{title}</b>{to}</div>
      {children}
      {count && <div className="mm-count">{count}</div>}
    </div>
  )
}
const counted = (t: string) => `${t.length} characters · ${t.split('\n').length} lines`

/** The phone beside each page, drawn by the server from the draft. */
function Phones({ page, d, preview, settings }: {
  page: MessagePage; d: Draft; preview: MessagesPreview | null; settings: OrgSettings
}) {
  const [again, setAgain] = useState(false)
  const [channel, setChannel] = useState<'sms' | 'email'>('sms')
  const [sample, setSample] = useState('flat')
  if (!preview) return <p className="msghint">Drawing…</p>

  if (page === 'broadcast') {
    const chans = settings.channels.length ? settings.channels.map((c) => c.channel) : ['sms' as const]
    // An organization with email alone shows email (the review).
    const ch = chans.includes(channel) ? channel : chans[0]
    const text = preview.broadcast?.[ch]?.[again ? 'again' : 'first'] ?? ''
    return (
      <>
        <div className="msgpvh">What goes out</div>
        <div className="mm-samples">
          <button type="button" className={!again ? 'on' : ''} onClick={() => setAgain(false)}>First broadcast</button>
          <button type="button" className={again ? 'on' : ''} onClick={() => setAgain(true)}>Re-broadcast</button>
        </div>
        {chans.length > 1 && (
          <div className="mm-samples">
            {chans.map((c) => (
              <button type="button" key={c} className={c === ch ? 'on' : ''}
                      onClick={() => setChannel(c)}>{c === 'sms' ? 'Text message' : 'Email'}</button>
            ))}
          </div>
        )}
        <Phone title={ch === 'sms' ? 'Text message' : 'Email'} to="to every member on the list" count={counted(text)}>
          <div className="mm-bubble">{text}</div>
        </Phone>
        <p className="msghint">An invented call, signed with your name. The middle is written by the system for each call.</p>
      </>
    )
  }

  if (page === 'going') {
    const list = (d.mode === 'words' ? preview.samples : preview.switches) ?? []
    const shown = list.find((s) => s.key === sample) ?? list[0]
    return (
      <>
        <div className="msgpvh">Try it on a call</div>
        <div className="mm-samples" role="group" aria-label="Try it on a call">
          {list.map((s) => (
            <button type="button" key={s.key} className={s.key === shown?.key ? 'on' : ''}
                    onClick={() => setSample(s.key)}>{s.name}</button>
          ))}
        </div>
        <Phone title="Text message" to="an invented call · unit MT-66, when he accepts" count={shown && counted(shown.text)}>
          <div className="mm-bubble">{shown?.text ?? '…'}</div>
        </Phone>
        {d.onAccept && d.thanks.trim() && (
          <p className="msghint">The last line is the thank-you, from Thank-you &amp; closed.</p>
        )}
      </>
    )
  }

  if (page === 'thanks') {
    const accepted = ((d.mode === 'words' ? preview.samples : preview.switches) ?? [])[0]?.text ?? ''
    const tail = accepted.split('\n')
    const bubbles: [string, string][] = [
      ['When he accepts — the end of it', tail.length > 4 ? `…\n${tail.slice(-3).join('\n')}` : accepted],
      ['Another message to him — a note', preview.note ?? ''],
    ]
    if (d.onClose) bubbles.push(['When the call is closed', preview.closed])
    if (d.removed) bubbles.push(['Taken off the call', preview.removed ?? ''])
    return (
      <>
        <div className="msgpvh">What he gets</div>
        <Phone title="Text messages" to="to unit MT-66">
          {bubbles.map(([cap, t]) => (
            <div key={cap}><div className="msgcap">{cap}</div><div className="mm-bubble">{t}</div></div>
          ))}
        </Phone>
      </>
    )
  }

  return (
    <>
      <div className="msgpvh">Three open calls, as it would go</div>
      <Phone title="Text message" to="to every member on the list" count={preview.combined ? counted(preview.combined) : undefined}>
        <div className="mm-bubble">{preview.combined ?? 'Every one of these calls starts “left out”, so nothing would go.'}</div>
      </Phone>
      <p className="msghint">
        The first has nobody going, the second one, the third two. The dispatcher can still change
        each line before it goes.
      </p>
    </>
  )
}
